import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { normalizeSearchText, toActiveKey } from '@/db/normalization'
import { createMachineInputSchema, machineSchema, updateMachineInputSchema } from '@/db/schemas'
import { createUuid } from '@/db/uuid'
import type { CreateMachineInput, Machine, UpdateMachineInput } from '@/db/types'
import { changedFields, nextCode, requireRecord, writeAuditLog } from './helpers'

export class MachineRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  async create(input: CreateMachineInput): Promise<Machine> {
    return executeDatabaseOperation('criar máquina', async () => {
      const data = createMachineInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.photos,
          this.database.counters,
          this.database.auditLogs,
        ],
        async () => {
          const client = requireRecord(await this.database.clients.get(data.clientId), 'Cliente')
          if (!client.active) {
            throw new DatabaseOperationError(
              'relationship',
              'Não é permitido criar máquina para cliente inativo.',
            )
          }
          const id = createUuid()
          if (data.photoId) {
            const photo = requireRecord(await this.database.photos.get(data.photoId), 'Foto')
            if (photo.machineId !== id) {
              throw new DatabaseOperationError(
                'relationship',
                'A foto informada não pertence à nova máquina.',
              )
            }
          }
          const now = new Date().toISOString()
          const machine = machineSchema.parse({
            ...data,
            id,
            code: await nextCode(this.database, 'machineCode', now),
            normalizedEquipment: normalizeSearchText(data.equipment),
            normalizedBrand: normalizeSearchText(data.brand),
            normalizedModel: normalizeSearchText(data.model),
            normalizedSerialChassis: normalizeSearchText(data.serialChassis),
            active: true,
            activeKey: 1,
            createdAt: now,
            updatedAt: now,
          })
          await this.database.machines.add(machine)
          await writeAuditLog(
            this.database,
            'machine',
            machine.id,
            'create',
            { code: machine.code, clientId: machine.clientId },
            now,
          )
          return machine
        },
      )
    })
  }

  async getById(id: string): Promise<Machine | undefined> {
    return executeDatabaseOperation('consultar máquina', async () => {
      const machine = await this.database.machines.get(id)
      return machine ? machineSchema.parse(machine) : undefined
    })
  }

  async listByClient(clientId: string, activeOnly = true, limit = 100): Promise<Machine[]> {
    return executeDatabaseOperation('listar máquinas do cliente', async () => {
      const pageSize = Math.min(Math.max(limit, 1), 100)
      const records = activeOnly
        ? await this.database.machines
            .where('[clientId+activeKey]')
            .equals([clientId, 1])
            .limit(pageSize)
            .toArray()
        : await this.database.machines.where('clientId').equals(clientId).limit(pageSize).toArray()
      return records.map((record) => machineSchema.parse(record))
    })
  }

  async list(
    options: { active?: boolean; clientId?: string; limit?: number; offset?: number } = {},
  ): Promise<Machine[]> {
    return executeDatabaseOperation('listar máquinas', async () => {
      const limit = Math.min(Math.max(options.limit ?? 50, 1), 500)
      const offset = Math.max(options.offset ?? 0, 0)
      let records: Machine[]
      if (options.clientId && options.active !== undefined) {
        records = await this.database.machines
          .where('[clientId+activeKey]')
          .equals([options.clientId, options.active ? 1 : 0])
          .offset(offset)
          .limit(limit)
          .toArray()
      } else if (options.clientId) {
        records = await this.database.machines
          .where('clientId')
          .equals(options.clientId)
          .offset(offset)
          .limit(limit)
          .toArray()
      } else if (options.active !== undefined) {
        records = await this.database.machines
          .where('activeKey')
          .equals(options.active ? 1 : 0)
          .offset(offset)
          .limit(limit)
          .toArray()
      } else {
        records = await this.database.machines
          .orderBy('updatedAt')
          .reverse()
          .offset(offset)
          .limit(limit)
          .toArray()
      }
      return records.map((record) => machineSchema.parse(record))
    })
  }

  async search(
    query: string,
    options: number | { limit?: number; includeInactive?: boolean } = 30,
  ): Promise<Machine[]> {
    return executeDatabaseOperation('pesquisar máquinas', async () => {
      const normalized = normalizeSearchText(query)
      if (!normalized) return []
      const pageSize = Math.min(
        Math.max(typeof options === 'number' ? options : (options.limit ?? 30), 1),
        500,
      )
      const includeInactive =
        typeof options === 'number' ? false : (options.includeInactive ?? false)
      const indexes = [
        'normalizedEquipment',
        'normalizedBrand',
        'normalizedModel',
        'normalizedSerialChassis',
      ] as const
      const groups = await Promise.all(
        indexes.map((index) =>
          this.database.machines.where(index).startsWith(normalized).limit(pageSize).toArray(),
        ),
      )
      const unique = new Map(groups.flat().map((record) => [record.id, record]))
      return [...unique.values()]
        .filter((record) => includeInactive || record.active)
        .slice(0, pageSize)
        .map((record) => machineSchema.parse(record))
    })
  }

  async findPotentialDuplicate(
    clientId: string,
    serialChassis: string,
    excludeId?: string,
  ): Promise<Machine | undefined> {
    return executeDatabaseOperation('verificar possível máquina duplicada', async () => {
      const normalizedSerial = normalizeSearchText(serialChassis)
      if (!normalizedSerial) return undefined
      const candidates = await this.database.machines
        .where('normalizedSerialChassis')
        .equals(normalizedSerial)
        .limit(20)
        .toArray()
      const duplicate = candidates.find(
        (machine) => machine.id !== excludeId && machine.clientId === clientId,
      )
      return duplicate ? machineSchema.parse(duplicate) : undefined
    })
  }

  async update(id: string, input: UpdateMachineInput): Promise<Machine> {
    return executeDatabaseOperation('editar máquina', async () => {
      const data = updateMachineInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.photos,
          this.database.auditLogs,
        ],
        async () => {
          const existing = machineSchema.parse(
            requireRecord(await this.database.machines.get(id), 'Máquina'),
          )
          const clientId = data.clientId ?? existing.clientId
          const client = requireRecord(await this.database.clients.get(clientId), 'Cliente')
          if (!client.active) {
            throw new DatabaseOperationError('relationship', 'A máquina exige cliente ativo.')
          }
          if (data.photoId) {
            const photo = requireRecord(await this.database.photos.get(data.photoId), 'Foto')
            if (photo.machineId !== id) {
              throw new DatabaseOperationError('relationship', 'A foto não pertence à máquina.')
            }
          }
          const now = new Date().toISOString()
          const machine = machineSchema.parse({
            ...existing,
            ...data,
            normalizedEquipment: normalizeSearchText(data.equipment ?? existing.equipment),
            normalizedBrand: normalizeSearchText(data.brand ?? existing.brand),
            normalizedModel: normalizeSearchText(data.model ?? existing.model),
            normalizedSerialChassis: normalizeSearchText(
              data.serialChassis ?? existing.serialChassis,
            ),
            updatedAt: now,
          })
          await this.database.machines.put(machine)
          await writeAuditLog(this.database, 'machine', id, 'update', changedFields(data), now)
          return machine
        },
      )
    })
  }

  async inactivate(id: string): Promise<Machine> {
    return executeDatabaseOperation('inativar máquina', async () =>
      this.database.transaction(
        'rw',
        [this.database.machines, this.database.auditLogs],
        async () => {
          const existing = machineSchema.parse(
            requireRecord(await this.database.machines.get(id), 'Máquina'),
          )
          if (!existing.active) return existing
          const now = new Date().toISOString()
          const machine = machineSchema.parse({
            ...existing,
            active: false,
            activeKey: toActiveKey(false),
            updatedAt: now,
          })
          await this.database.machines.put(machine)
          await writeAuditLog(this.database, 'machine', id, 'inactivate', {}, now)
          return machine
        },
      ),
    )
  }

  async reactivate(id: string): Promise<Machine> {
    return executeDatabaseOperation('reativar máquina', async () =>
      this.database.transaction(
        'rw',
        [this.database.clients, this.database.machines, this.database.auditLogs],
        async () => {
          const existing = machineSchema.parse(
            requireRecord(await this.database.machines.get(id), 'Máquina'),
          )
          if (existing.active) return existing
          const client = requireRecord(
            await this.database.clients.get(existing.clientId),
            'Cliente',
          )
          if (!client.active) {
            throw new DatabaseOperationError(
              'relationship',
              'Ative o cliente antes de reativar esta máquina.',
            )
          }
          const now = new Date().toISOString()
          const machine = machineSchema.parse({
            ...existing,
            active: true,
            activeKey: toActiveKey(true),
            updatedAt: now,
          })
          await this.database.machines.put(machine)
          await writeAuditLog(
            this.database,
            'machine',
            id,
            'update',
            { fields: ['active'], from: false, to: true },
            now,
          )
          return machine
        },
      ),
    )
  }

  async delete(id: string): Promise<void> {
    return executeDatabaseOperation('apagar máquina', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.machines,
          this.database.inspections,
          this.database.photos,
          this.database.auditLogs,
        ],
        async () => {
          const machine = machineSchema.parse(
            requireRecord(await this.database.machines.get(id), 'Máquina'),
          )
          if (machine.active) {
            throw new DatabaseOperationError(
              'invalid_state',
              'Máquina ativa não pode ser apagada; inative primeiro.',
            )
          }
          const [inspectionCount, photoCount] = await Promise.all([
            this.database.inspections.where('machineId').equals(id).count(),
            this.database.photos.where('machineId').equals(id).count(),
          ])
          if (inspectionCount || photoCount) {
            throw new DatabaseOperationError(
              'protected_record',
              `Máquina possui ${inspectionCount} inspeções e ${photoCount} fotos vinculadas.`,
            )
          }
          const now = new Date().toISOString()
          await this.database.machines.delete(id)
          await writeAuditLog(this.database, 'machine', id, 'delete', {}, now)
        },
      ),
    )
  }
}
